import React, { useState, useEffect } from 'react';
import {
    TableBody,
    TableCell,
    TableRow,
    Table,
    TableHeader,
    TableHeaderCell,
    TableCellLayout,
    Button,
    Dialog, DialogSurface, DialogContent, DialogTitle, DialogActions
} from "@fluentui/react-components";
import { EditRegular, DeleteRegular } from "@fluentui/react-icons";
import { useMsal } from '@azure/msal-react';

import LoadingPage from './LoadingPage';
import { useAuthHeader } from './getAuthHeader';
import { useSignInState } from './signInState';

interface UserTableProps {
    onSelectUser: (user: UserType) => void;
    CustomerID: number | undefined;
}

const UserTable: React.FC<UserTableProps> = ({ onSelectUser, CustomerID }) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const { signInState, setSignInState } = useSignInState();
    const [users, setUsers] = useState<UserType[]>([]);
    const [hasUsers, setHasUsers] = useState(false);
    const [isPending, setIsPending] = useState(true);
    const [authHeader, setAuthHeader] = useState<RequestInit>({});
    const { inProgress, accounts } = useMsal();

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

    const getAuthHeader = useAuthHeader('AdminUser', 'POST');

    useEffect(() => {
        const fetchAuthHeader = async () => {
            const header = await getAuthHeader();
            setAuthHeader(header);
        };
        if (inProgress === "none" && accounts.length > 0) {
            fetchAuthHeader();
        }
    }, [inProgress, accounts]);

    useEffect(() => {
        // Fetch users based on the CustomerID from the /getusers API
        function isRequestInit(obj: any): obj is RequestInit {
            return obj && typeof obj === 'object' && 'method' in obj && 'headers' in obj
        }
        if (isRequestInit(authHeader)) {
            fetch(`${apiUrl}admin/getallusers`, {
                ...authHeader,
                body: JSON.stringify({ "CustomerID": CustomerID })
            })
                .then(response => response.json())
                .then(data => {

                    if (data.length === 0) {
                        console.error('No users found for the CustomerID:', CustomerID);
                        setHasUsers(false);
                        setIsPending(false);
                    } else {
                        setHasUsers(true);
                        setUsers(data);
                        setIsPending(false);
                        console.log("Users: ", data);
                    }

                })
                .catch(error => console.error(error));

        }
    }, [authHeader, CustomerID]);

    /**
     * Delete a user by making an API call.
     * Then remove the deleted user from `users` state to update the UI.
     */
    const handleDeleteUser = async () => {
        if (selectedUserId === null) return;

        try {
            const response = await fetch(`${apiUrl}admin/deleteuser`, {
                ...authHeader,
                method: 'DELETE',
                body: JSON.stringify({ CustomerID: CustomerID, UserID: selectedUserId }),
            });

            if (!response.ok) {
                console.error('Server error when deleting user:', response.statusText);
                return;
            }
            console.log("Deleting user: ", selectedUserId);
            // Remove from local state so we don’t need to refetch everything
            setUsers((prev) => prev.filter((u) => u.UserID !== selectedUserId));
        } catch (error) {
            console.error('Network error when deleting user:', error);
        } finally {
            setIsDialogOpen(false);
            setSelectedUserId(null);
        }
    };

    const closeDialog = () => {
        setIsDialogOpen(false);
        setSelectedUserId(null);
    };

    if (isPending) {
        return <LoadingPage message="Loading users..." />;
    }

    if (!hasUsers) {
        return (
            <div>
                <h3>No users found for the CustomerID: {CustomerID}</h3>
            </div>
        );
    }

    // Render table of users
    return (
        <div style={{ margin: '20px' }}>
            <Table aria-label="User details" style={{ minWidth: "510px", maxWidth: "1300px" }}>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell>User</TableHeaderCell>
                        <TableHeaderCell>Email</TableHeaderCell>
                        <TableHeaderCell>State</TableHeaderCell>
                        <TableHeaderCell>Actions</TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {users.map((user) => (
                        <TableRow key={user.UserID}>
                            {/* 1. Display User/Name */}
                            <TableCell>
                                <TableCellLayout truncate>
                                    {/* Fallback to user.UserName if DisplayName is not present */}
                                    {user.DisplayName || user.UserName}
                                </TableCellLayout>
                            </TableCell>
                            {/* 2. Display Email */}
                            <TableCell>
                                <TableCellLayout truncate>
                                    {user.Email}
                                </TableCellLayout>
                            </TableCell>
                            {/* 3. Display IsActive */}
                            <TableCell>
                                <TableCellLayout truncate>
                                    {user.IsActiveForCustomer ? 'Active' : 'Disabled'}
                                </TableCellLayout>
                            </TableCell>
                            {/* 4. Actions (Edit + Delete) */}
                            <TableCell>
                                <TableCellLayout>
                                    <Button
                                        icon={<EditRegular />}
                                        aria-label="Edit"
                                        onClick={() => onSelectUser(user)}
                                    />
                                    {signInState.userInfo[0].UserID !== user.UserID &&
                                        <Button
                                            icon={<DeleteRegular />}
                                            appearance="subtle"
                                            aria-label="Delete"
                                            onClick={() => {
                                                setSelectedUserId(user.UserID);
                                                setIsDialogOpen(true);
                                            }}
                                        />
                                    }
                                </TableCellLayout>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {/* Delete User Confirmation Dialog */}
            <Dialog
                open={isDialogOpen}
                onOpenChange={(_, { open }) => setIsDialogOpen(open)}
            >
                <DialogSurface>
                    <DialogTitle>
                        Confirm Deletion
                    </DialogTitle>
                    <DialogContent>
                        <p>Are you sure you want to delete this user? This action cannot be undone.</p>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeDialog}>Cancel</Button>
                        <Button appearance="primary" onClick={handleDeleteUser}>
                            Delete
                        </Button>
                    </DialogActions>
                </DialogSurface>
            </Dialog>
        </div>
    );
};

export default UserTable;
